import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  onSnapshot, 
  setDoc 
} from 'firebase/firestore';
import { 
  Activity, 
  Plus, 
  LayoutDashboard, 
  Send, 
  ChevronDown, 
  Trash2, 
  Terminal, 
  Edit3, 
  Globe
} from 'lucide-react';

/* global __firebase_config, __app_id, __initial_auth_token */

/**
 * Pengaturan Konfigurasi Firebase
 * Menggunakan variabel lingkungan dari Canvas atau fallback ke konfigurasi default
 */
const getFirebaseConfig = () => {
  try {
    if (typeof __firebase_config !== 'undefined' && __firebase_config) {
      return JSON.parse(__firebase_config);
    }
  } catch (e) {
    console.error("Gagal memproses __firebase_config:", e);
  }
  // Fallback config (berdasarkan data yang Anda berikan sebelumnya)
  return {
    apiKey: "AIzaSyB6TDbWDUFhwfYSCJ3ZIfvknlOUySa8pKg",
    authDomain: "reportbos-af377.firebaseapp.com",
    projectId: "reportbos-af377",
    storageBucket: "reportbos-af377.firebasestorage.app",
    messagingSenderId: "236618664749",
    appId: "1:236618664749:web:b507cafd0717ffbec7a1a4"
  };
};

const firebaseConfig = getFirebaseConfig();
const appId = typeof __app_id !== 'undefined' ? __app_id : 'reportbos-app';
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- Komponen Pendukung ---

const Badge = ({ status }) => {
  const styles = {
    'aktif': 'bg-green-500/10 text-green-400 border-green-500/20',
    'tidak aktif': 'bg-red-500/10 text-red-400 border-red-500/20',
    'nawala': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    'blokir': 'bg-slate-500/10 text-slate-400 border-slate-500/20'
  };
  return (
    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${styles[status] || styles['blokir']}`}>
      {status}
    </span>
  );
};

const ItemCard = ({ label, item }) => {
  if (!item?.name) return (
    <div className="p-3 rounded-xl border border-dashed border-slate-800 flex items-center justify-center opacity-40 h-24 text-center">
      <span className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">{label} Kosong</span>
    </div>
  );

  const isCritical = item.status === 'blokir' || item.status === 'nawala';
  const indiColor = item.indiwtf_status === 'blokir' ? 'text-orange-400 font-bold' : 'text-cyan-500';

  return (
    <div className={`p-4 rounded-2xl border transition-all bg-slate-800/40 backdrop-blur-md shadow-xl ${isCritical ? 'border-red-500/50 animate-pulse' : 'border-slate-700/40'}`}>
      <div className="flex justify-between items-start mb-2">
        <span className="text-[10px] text-slate-500 font-black uppercase tracking-wider">{label}</span>
        <Badge status={item.status} />
      </div>
      <p className="text-white font-bold truncate text-sm" title={item.name}>{item.name}</p>
      <div className="mt-3 flex items-center justify-between border-t border-slate-700/30 pt-2">
        <span className="text-[9px] text-slate-500 uppercase font-black">IndiWTF</span>
        <span className={`text-[10px] uppercase font-bold ${indiColor}`}>{item.indiwtf_status}</span>
      </div>
      {item.note && (
        <div className="mt-2 bg-black/20 p-1.5 rounded-lg">
          <p className="text-[10px] text-slate-400 italic leading-tight">"{item.note}"</p>
        </div>
      )}
    </div>
  );
};

const StoreGroup = ({ group }) => {
  const activeDomains = group.domains?.filter(d => d.name) || [];
  
  return (
    <div className="space-y-6 mb-16 relative">
      <div className="flex items-center gap-4">
        <div className="h-1 w-8 bg-cyan-500 rounded-full shadow-[0_0_10px_rgba(34,211,238,0.5)]"></div>
        <h2 className="text-xl font-black italic uppercase text-white tracking-tight">{group.displayName || 'Toko Tanpa Nama'}</h2>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {activeDomains.length > 0 ? (
          activeDomains.map((d, i) => <ItemCard key={i} label={`Domain ${i+1}`} item={d} />)
        ) : (
          <div className="lg:col-span-5 p-8 border-2 border-dashed border-slate-800 rounded-3xl flex flex-col items-center justify-center text-slate-600">
            <Globe className="w-8 h-8 mb-2 opacity-20" />
            <p className="text-xs font-bold uppercase tracking-widest">Belum ada domain aktif</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-800/50 pt-6">
        <ItemCard label="ADS Channel" item={group.ads} />
        <ItemCard label="AMP Mirror" item={group.amp} />
        <ItemCard label="Cache Service" item={group.cache} />
      </div>
    </div>
  );
};

// --- Komponen Utama ---

export default function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [telegramConfig, setTelegramConfig] = useState({ 
    token: '', 
    targets: [{chatId: '', threadId: ''}, {chatId: '', threadId: ''}] 
  });
  const [logs, setLogs] = useState([]);

  const log = useCallback((msg, type = 'info') => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [`[${time}] ${msg}`, ...prev].slice(0, 50));
  }, []);

  // 1. Inisialisasi Autentikasi
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (e) { 
        log(`Auth Error: ${e.message}`, 'error'); 
      }
    };
    initAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
    });
    return () => unsub();
  }, [log]);

  // 2. Sinkronisasi Data Firestore (Real-time)
  useEffect(() => {
    if (!user) return;
    const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'dashboard');
    
    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setGroups(data.groups || []);
        if (data.telegram) setTelegramConfig(data.telegram);
        log("Data Cloud disinkronkan.");
      } else {
        log("Inisialisasi data baru di Cloud...");
        const initial = [{ 
          displayName: 'CONTOH TOKO', 
          domains: [{name: '', status: 'aktif', note: '', indiwtf_status: 'aktif'}],
          ads: {name: '', status: 'aktif', note: '', indiwtf_status: 'aktif'},
          amp: {name: '', status: 'aktif', note: '', indiwtf_status: 'aktif'},
          cache: {name: '', status: 'aktif', note: '', indiwtf_status: 'aktif'}
        }];
        setGroups(initial);
      }
      setIsLoading(false);
    }, (err) => log(`Firestore Error: ${err.message}`, 'error'));

    return () => unsub();
  }, [user, log]);

  // 3. Auto-Save Debounced (Simpan Otomatis)
  useEffect(() => {
    if (isLoading || !user) return;
    const timer = setTimeout(() => {
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'dashboard');
      setDoc(docRef, { groups, telegram: telegramConfig }, { merge: true })
        .catch(e => log(`Save Error: ${e.message}`, 'error'));
    }, 2000);
    return () => clearTimeout(timer);
  }, [groups, telegramConfig, isLoading, user, log]);

  // Handlers
  const updateItem = (gIdx, cat, field, val, iIdx = null) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== gIdx) return g;
      if (cat === 'displayName') return { ...g, displayName: val };
      if (iIdx !== null) {
        const newArr = [...g[cat]];
        newArr[iIdx] = { ...newArr[iIdx], [field]: val };
        return { ...g, [cat]: newArr };
      }
      return { ...g, [cat]: { ...g[cat], [field]: val } };
    }));
  };

  const updateTelegram = (field, val, tIdx = null) => {
    setTelegramConfig(prev => {
        if (field === 'token') return { ...prev, token: val };
        const newTargets = [...prev.targets];
        newTargets[tIdx] = { ...newTargets[tIdx], [field]: val };
        return { ...prev, targets: newTargets };
    });
  };

  const addNewStore = () => {
    const empty = { name: '', status: 'aktif', note: '', indiwtf_status: 'aktif' };
    setGroups([...groups, { 
      displayName: 'TOKO BARU', 
      domains: [{ ...empty }], 
      ads: { ...empty }, amp: { ...empty }, cache: { ...empty } 
    }]);
    log("Toko baru ditambahkan.");
  };

  const deleteStore = (idx) => {
    if(window.confirm("Hapus toko ini secara permanen?")) {
        setGroups(groups.filter((_, i) => i !== idx));
        log("Toko dihapus dari sistem.");
    }
  };

  const addDomain = (gi) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== gi) return g;
      const domains = g.domains || [];
      if (domains.length >= 10) return g;
      return { ...g, domains: [...domains, { name: '', status: 'aktif', note: '', indiwtf_status: 'aktif' }] };
    }));
  };

  const sendTelegramReport = async () => {
    if (!telegramConfig.token) return log("Bot Token Telegram kosong!", "error");
    const activeTargets = telegramConfig.targets.filter(t => t.chatId);
    if (activeTargets.length === 0) return log("Chat ID Target belum diisi!", "error");

    log("Mengirim laporan ke Telegram...");
    let msg = `🚀 *STATUS DASHBOARD REPORT*\n📅 ${new Date().toLocaleString('id-ID')}\n\n`;
    groups.forEach(g => {
      msg += `🏢 *${g.displayName}*\n`;
      (g.domains || []).filter(d => d.name).forEach((d, i) => {
        const emoji = d.status === 'aktif' ? '🟢' : (d.status === 'blokir' ? '🚫' : '🟡');
        msg += `${emoji} D${i+1}: ${d.name}\n`;
      });
      const check = (i) => i?.status === 'aktif' ? '✅' : '❌';
      msg += `📡 ADS: ${check(g.ads)} | AMP: ${check(g.amp)} | CACHE: ${check(g.cache)}\n`;
      msg += `─────────────────\n`;
    });

    for (const target of activeTargets) {
      try {
        const url = `https://api.telegram.org/bot${telegramConfig.token}/sendMessage`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            chat_id: target.chatId, 
            message_thread_id: target.threadId || undefined, 
            text: msg, 
            parse_mode: 'Markdown' 
          })
        });
        const data = await res.json();
        if (data.ok) log(`Laporan terkirim ke Chat ID: ${target.chatId}`);
        else log(`Gagal kirim ke ${target.chatId}: ${data.description}`, "error");
      } catch (e) { log(`Koneksi Gagal: ${e.message}`, "error"); }
    }
  };

  if (isLoading) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-[#0f172a] text-white">
      <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-cyan-500 animate-pulse">Menghubungkan ke Cloud...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0f172a] bg-[radial-gradient(circle_at_top_right,#1e293b,#0f172a)] text-slate-200 font-sans pb-20 overflow-x-hidden">
      
      {/* Header */}
      <header className="border-b border-slate-800/50 p-6 sticky top-0 z-50 bg-slate-900/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
              <Activity className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
                <h1 className="text-xl font-black italic uppercase tracking-tighter text-white">Status <span className="text-cyan-500">PRO</span></h1>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Live Cloud Sync</span>
                </div>
            </div>
          </div>
          <button 
            onClick={addNewStore} 
            className="bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-black px-4 py-2 rounded-lg flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-cyan-900/20"
          >
            <Plus className="w-3 h-3" /> TAMBAH TOKO
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full p-6 grid grid-cols-1 lg:grid-cols-4 gap-8">
        
        {/* Konten Utama (Status Cards) */}
        <div className="lg:col-span-3 space-y-12">
          {groups.length > 0 ? groups.map((g, i) => (
            <div key={i} className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-1 w-8 bg-cyan-500 rounded-full shadow-[0_0_10px_rgba(34,211,238,0.5)]"></div>
                <h2 className="text-xl font-black italic uppercase text-white tracking-tight">{g.displayName}</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {(g.domains || []).filter(d => d.name).length > 0 ? (
                    (g.domains || []).filter(d => d.name).map((d, di) => <ItemCard key={di} label={`Domain ${di+1}`} item={d} />)
                ) : (
                    <div className="lg:col-span-5 p-6 border border-dashed border-slate-800 rounded-2xl text-center opacity-30">
                        <p className="text-[10px] font-bold uppercase tracking-widest italic">Belum ada domain yang diinput</p>
                    </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-800/50 pt-4">
                <ItemCard label="ADS Channel" item={g.ads} />
                <ItemCard label="AMP Mirror" item={g.amp} />
                <ItemCard label="Cache Service" item={g.cache} />
              </div>
            </div>
          )) : (
            <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-slate-800 rounded-[3rem] opacity-20">
                <LayoutDashboard className="w-16 h-16 mb-4" />
                <p className="font-black uppercase tracking-[0.3em]">Dashboard Kosong</p>
            </div>
          )}
        </div>

        {/* Panel Kontrol (Sidebar) */}
        <aside className="space-y-6">
          
          {/* Telegram Bot Config */}
          <div className="p-5 rounded-3xl border border-slate-800 bg-slate-900/40 backdrop-blur-md shadow-2xl space-y-4">
            <h3 className="text-[10px] font-black uppercase text-cyan-500 tracking-widest flex items-center gap-2">
              <Send className="w-3 h-3" /> Konfigurasi Laporan
            </h3>
            <div className="space-y-3">
                <div>
                    <label className="text-[8px] font-bold text-slate-500 uppercase block mb-1">Telegram Bot Token</label>
                    <input 
                        type="password" 
                        placeholder="Bot Token" 
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-[10px] outline-none focus:ring-1 focus:ring-cyan-500 transition-all" 
                        value={telegramConfig.token} 
                        onChange={e => updateTelegram('token', e.target.value)} 
                    />
                </div>
                {telegramConfig.targets.map((t, ti) => (
                    <div key={ti} className="p-3 bg-black/30 rounded-xl border border-slate-800 space-y-2">
                        <p className="text-[9px] font-bold text-slate-600 uppercase">Target #{ti+1}</p>
                        <input 
                            placeholder="Chat ID" 
                            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-[10px] outline-none focus:border-cyan-500 transition-all" 
                            value={t.chatId} 
                            onChange={e => updateTelegram('chatId', e.target.value, ti)} 
                        />
                        <input 
                            placeholder="Thread ID (Opsional)" 
                            className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-[10px] outline-none focus:border-cyan-500 transition-all" 
                            value={t.threadId} 
                            onChange={e => updateTelegram('threadId', e.target.value, ti)} 
                        />
                    </div>
                ))}
            </div>
            <button 
                onClick={sendTelegramReport} 
                className="w-full bg-slate-800 hover:bg-slate-700 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-slate-700"
            >
                Kirim Laporan Sekarang
            </button>
          </div>

          {/* Toko Editor */}
          <div className="p-5 rounded-3xl border border-slate-800 bg-slate-900/40 backdrop-blur-md shadow-2xl space-y-4">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
              <Edit3 className="w-3 h-3" /> Editor Brand
            </h3>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-800">
              {groups.map((g, gi) => (
                <details key={gi} className="group bg-slate-950/40 rounded-xl border border-slate-800 overflow-hidden">
                  <summary className="p-3 cursor-pointer hover:bg-slate-800 flex justify-between items-center transition-all">
                    <span className="text-[10px] font-bold uppercase truncate text-slate-300">{g.displayName || 'Toko Baru'}</span>
                    <ChevronDown className="w-3 h-3 text-slate-600 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="p-3 border-t border-slate-800 space-y-5">
                    <div className="flex gap-2">
                      <input 
                        className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-[10px] text-cyan-500 font-bold" 
                        value={g.displayName} 
                        onChange={e => updateItem(gi, 'displayName', '', e.target.value)} 
                      />
                      <button 
                        onClick={() => deleteStore(gi)} 
                        className="text-red-500 hover:bg-red-500/10 p-1.5 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold uppercase text-slate-600 tracking-widest italic">Domains</span>
                        <button onClick={() => addDomain(gi)} className="text-[9px] text-cyan-500 font-black hover:text-white transition-all">+ ADD</button>
                      </div>
                      {g.domains.map((d, di) => (
                        <div key={di} className="p-2 bg-black/40 rounded-xl border border-slate-800 space-y-2">
                          <input 
                            placeholder="Alamat URL" 
                            className="w-full bg-transparent border-b border-slate-800 text-[9px] text-white outline-none focus:border-cyan-500 py-1" 
                            value={d.name} 
                            onChange={e => updateItem(gi, 'domains', 'name', e.target.value, di)} 
                          />
                          <div className="flex gap-1">
                            <select 
                                className="flex-1 bg-slate-900 border border-slate-800 text-[9px] rounded-lg p-1 outline-none text-slate-400" 
                                value={d.status} 
                                onChange={e => updateItem(gi, 'domains', 'status', e.target.value, di)}
                            >
                              <option value="aktif">🟢 AKTIF</option>
                              <option value="nawala">🟡 NAWALA</option>
                              <option value="blokir">🔴 BLOKIR</option>
                            </select>
                            <select 
                                className="flex-1 bg-slate-900 border border-slate-800 text-[9px] rounded-lg p-1 outline-none text-slate-400" 
                                value={d.indiwtf_status} 
                                onChange={e => updateItem(gi, 'domains', 'indiwtf_status', e.target.value, di)}
                            >
                              <option value="aktif">WTF: OK</option>
                              <option value="blokir">WTF: BK</option>
                            </select>
                          </div>
                          <input 
                            placeholder="Tambahkan catatan..." 
                            className="w-full bg-transparent text-[8px] text-slate-600 outline-none" 
                            value={d.note} 
                            onChange={e => updateItem(gi, 'domains', 'note', e.target.value, di)} 
                          />
                        </div>
                      ))}
                    </div>

                    <div className="pt-2 border-t border-slate-800/50 space-y-3">
                      <span className="text-[9px] font-bold uppercase text-slate-600 italic">Infrastruktur Sistem</span>
                      {['ads', 'amp', 'cache'].map(c => (
                        <div key={c} className="p-2 bg-black/20 rounded-xl border border-slate-800/30 space-y-1">
                          <div className="flex gap-1">
                            <input 
                                placeholder={c.toUpperCase()} 
                                className="flex-1 bg-transparent border-b border-slate-800 text-[9px] py-1 outline-none text-white" 
                                value={g[c]?.name} 
                                onChange={e => updateItem(gi, c, 'name', e.target.value)} 
                            />
                            <select 
                                className="bg-slate-900 border border-slate-800 text-[9px] rounded-lg p-1 outline-none text-slate-400" 
                                value={g[c]?.status} 
                                onChange={e => updateItem(gi, c, 'status', e.target.value)}
                            >
                              <option value="aktif">OK</option>
                              <option value="blokir">ERR</option>
                            </select>
                          </div>
                          <input 
                            placeholder="Note infrastruktur..." 
                            className="w-full bg-transparent text-[8px] text-slate-600 outline-none" 
                            value={g[c]?.note} 
                            onChange={e => updateItem(gi, c, 'note', e.target.value)} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Terminal Logs */}
          <div className="p-5 bg-black rounded-3xl border border-slate-800 shadow-2xl">
            <p className="text-[9px] font-black text-slate-600 uppercase mb-3 flex items-center gap-2">
              <Terminal className="w-3 h-3 text-cyan-900" /> System Terminal
            </p>
            <div className="h-40 overflow-y-auto font-mono text-[9px] text-slate-500 space-y-1 custom-scrollbar pr-2">
              {logs.length > 0 ? logs.map((l, i) => (
                <div key={i} className="flex gap-2 border-b border-white/5 pb-1">
                  <span className="text-cyan-900 shrink-0 select-none">#</span>
                  <span className={l.includes('Error') ? 'text-red-900' : (l.includes('Cloud') ? 'text-cyan-700' : '')}>
                    {l}
                  </span>
                </div>
              )) : (
                <div className="italic text-slate-900">Awaiting system activity...</div>
              )}
            </div>
          </div>
        </aside>
      </main>
      
      {/* Footer Ringan */}
      <footer className="max-w-7xl mx-auto px-6 opacity-20 text-center mt-auto">
          <p className="text-[8px] font-bold tracking-[0.4em] uppercase">Status Dashboard Pro System v2.5.0</p>
      </footer>
    </div>
  );
}